"use client";

import React, { useState } from "react";
import Image from "next/image";

const OfficeGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const officeImages = [
    { src: "/images/custom_images_office_1.webp", alt: "Office Image 1" },
    { src: "/images/custom_images_office_2.webp", alt: "Office Image 2" },
    { src: "/images/custom_images_office_3.webp", alt: "Office Image 3" },
    { src: "/images/custom_images_office_4.webp", alt: "Office Image 4" },
    { src: "/images/custom_images_office_5.webp", alt: "Office Image 5" },
    { src: "/images/custom_images_office_6.webp", alt: "Office Image 6" },
    { src: "/images/custom_images_office_7.webp", alt: "Office Image 7" },
    { src: "/images/custom_images_office_8.webp", alt: "Office Image 8" },
    { src: "/images/custom_images_office_9.webp", alt: "Office Image 9" },
    { src: "/images/custom_images_office_10.webp", alt: "Office Image 10" },
    { src: "/images/custom_images_office_11.webp", alt: "Office Image 11" },
    { src: "/images/custom_images_office_12.webp", alt: "Office Image 12" },
    { src: "/images/custom_images_office_13.webp", alt: "Office Image 13" },
    { src: "/images/custom_images_office_14.webp", alt: "Office Image 14" },
    { src: "/images/custom_images_office_15.webp", alt: "Office Image 15" },
    { src: "/images/custom_images_office_16.webp", alt: "Office Image 16" },
    { src: "/images/custom_images_office_17.webp", alt: "Office Image 17" },
  ];

  return (
    <section className="w-full bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <h2
          className="
            text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 text-center mb-12
            after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:my-6
            after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
          "
        >
          Office Gallery
        </h2>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {officeImages.map((image, index) => (
            <div
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              onClick={() => setSelectedImage(index)}
            >
              <div className="aspect-square relative">
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-300"
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <span className="text-gray-700 text-xl">🔍</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal/Lightbox */}
        {selectedImage !== null && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white text-2xl hover:text-gray-300 transition-colors"
                aria-label="Close gallery"
              >
                ✕
              </button>
              <div className="relative">
                <Image
                  src={officeImages[selectedImage].src}
                  alt={officeImages[selectedImage].alt}
                  width={800}
                  height={600}
                  className="object-contain max-h-[80vh] rounded-lg"
                  sizes="80vw"
                />
              </div>
              
              {/* Navigation buttons */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(selectedImage > 0 ? selectedImage - 1 : officeImages.length - 1);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all"
                aria-label="Previous image"
              >
                ←
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(selectedImage < officeImages.length - 1 ? selectedImage + 1 : 0);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all"
                aria-label="Next image"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OfficeGallery;